import { Flex, Link, TextField, DatePicker, DialogContainer, Dialog, Content, Heading, Button, View } from "@adobe/react-spectrum"
import styles from './invoice_detail_section.module.css'
import { useState } from "react"
import JournalDropDown from "./formComponents/JournalDropDown"
import InvoiceCurrencyDropDown from "./formComponents/CurrencyDropDown"
import moment from "moment"
import { parseDate } from "@internationalized/date"
import { useTranslation } from "react-i18next"
interface IInvoiceDetailSection {
    invoice: any
    onChange: (data: any) => void
    isEdit: any
    isLocked?: boolean
    SELECTED_INVOICE?: any
}
const InvoiceDetailSection: React.FC<IInvoiceDetailSection> = ({ invoice, onChange, isEdit, isLocked, SELECTED_INVOICE }) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const [pendingJournal, setPendingJournal] = useState<any | null>(null);
    const [isJournalConfirmOpen, setIsJournalConfirmOpen] = useState(false);
    const { t } = useTranslation("invoice");
    const _t = (key: string, defaultValue: string) =>
        t(`add_update_invoice.${key}`, { ns: "invoice", defaultValue });
    const _tt = (key: string, defaultValue: string) =>
        t(`invoice_common.${key}`, { ns: "invoice", defaultValue });

    const baseType = String(invoice?.baseType || SELECTED_INVOICE?.baseType || '');
    const isInvoiceDoc = baseType === '1';
    const isFormEditable = !!isEdit || !invoice?.id; // editable in Add or Update, read-only in Detail view

    const secondaryDateLabel = (() => {
        if (isInvoiceDoc) return _t("due_date", "Due Date");
        if (baseType === '4') return _t("valid_until", "Valid until");
        return _t("exp_delivery", "Exp. delivery");
    })();

    const readDateValue = (value: any) =>
        value ? parseDate(moment(value).format("YYYY-MM-DD")) : undefined;

    const normalizedCurrencyRate = String(invoice?.currencyRate ?? "").trim();
    const parsedCurrencyRate = Number(normalizedCurrencyRate);
    const showExchangeRateHint =
        normalizedCurrencyRate !== "" &&
        Number.isFinite(parsedCurrencyRate) &&
        parsedCurrencyRate > 0 &&
        parsedCurrencyRate !== 1;

    const exchangeRateLabel = (
        <Flex alignItems="baseline" gap="size-100" wrap UNSAFE_className={styles.exchange_rate_label}>
            <span className={styles.exchange_rate_label_text}>
                {showExchangeRateHint
                    ? _t("exchange_rate_short", "Exch. rate")
                    : _t("currency", "Currency")}
            </span>
            {showExchangeRateHint && (
                <span className={styles.exchange_rate_hint}>
                    ({normalizedCurrencyRate})
                </span>
            )}
        </Flex>
    );

    const addDaysToIsoDate = (isoDate: string, days: number): string => {
        const [year, month, day] = String(isoDate).split("-").map(Number);
        if (!year || !month || !day) return isoDate;
        const base = new Date(Date.UTC(year, month - 1, day));
        if (Number.isNaN(base.getTime())) return isoDate;
        base.setUTCDate(base.getUTCDate() + Math.max(0, days));
        const y = base.getUTCFullYear();
        const m = String(base.getUTCMonth() + 1).padStart(2, "0");
        const d = String(base.getUTCDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const handleIssueDateChange = (dateValue: any) => {
        const nextIssuedDate = dateValue?.toString();
        const customerDueDaysNumeric = Math.max(0, Number(invoice?.customerDueDays) || 0);
        const defaultDueDaysNumeric = Math.max(0, Number(invoice?.invoiceDefaultDueDays) || 0);
        const hasCustomerDueDays = customerDueDaysNumeric > 0;
        const hasDefaultDueDays = defaultDueDaysNumeric > 0;
        const dueDays = Math.max(
            0,
            hasCustomerDueDays ? customerDueDaysNumeric : (hasDefaultDueDays ? defaultDueDaysNumeric : 0)
        );

        if (isInvoiceDoc && nextIssuedDate && (hasCustomerDueDays || hasDefaultDueDays)) {
            onChange({
                ...invoice,
                issuedDate: nextIssuedDate,
                dueDate: addDaysToIsoDate(nextIssuedDate, dueDays),
            });
            return;
        }

        onChange({ ...invoice, issuedDate: nextIssuedDate });
    };

    const getSecondaryDateValue = () => {
        if (isInvoiceDoc) return readDateValue(invoice?.dueDate);
        return readDateValue(invoice?.periodTo || invoice?.dueDate);
    };

    const setSecondaryDate = (dateValue: any) => {
        const nextDate = dateValue?.toString();
        if (isInvoiceDoc) {
            onChange({ ...invoice, dueDate: nextDate });
            return;
        }
        // For orders/offers use periodTo as dedicated second date,
        // keep dueDate in sync for backend compatibility.
        onChange({ ...invoice, periodTo: nextDate, dueDate: nextDate });
    };

    const getJournalValue = (journal: any, ...keys: string[]) => {
        for (const key of keys) {
            const val = journal?.[key];
            if (val !== undefined && val !== null && val !== '') return val;
        }
        return undefined;
    };

    const remapTransactionAccountsByVatTitle = (transactions: any[], nextHeaderAccounts: any) => {
        return transactions.map((txn: any) => {
            const vatTitle = String(txn?.VATTitle ?? txn?.vatTitle ?? "").toUpperCase();
            if (!vatTitle) return txn;

            if (vatTitle === "N") {
                return {
                    ...txn,
                    account: nextHeaderAccounts.accountBase ?? txn?.account,
                    accountName: nextHeaderAccounts.accountBaseName ?? txn?.accountName,
                };
            }

            if (vatTitle === "T") {
                return {
                    ...txn,
                    account: nextHeaderAccounts.accountVAT ?? nextHeaderAccounts.accountTotal ?? txn?.account,
                    accountName:
                        nextHeaderAccounts.accountVATName ??
                        nextHeaderAccounts.accountTotalName ??
                        txn?.accountName,
                };
            }

            if (vatTitle === "S") {
                return {
                    ...txn,
                    account: nextHeaderAccounts.accountTotal ?? txn?.account,
                    accountName: nextHeaderAccounts.accountTotalName ?? txn?.accountName,
                };
            }

            if (vatTitle === "R") {
                return {
                    ...txn,
                    account: nextHeaderAccounts.accountRound ?? nextHeaderAccounts.accountTotal ?? txn?.account,
                    accountName:
                        nextHeaderAccounts.accountRoundName ??
                        nextHeaderAccounts.accountTotalName ??
                        txn?.accountName,
                };
            }

            return txn;
        });
    };

    const commitJournalChange = (journal: any, shouldRemap: boolean) => {
        if (!journal) return;

        const treeHandle = getJournalValue(journal, 'treeHandle', 'treehandle');
        const nextHeaderAccounts = {
            accountBase: getJournalValue(journal, 'accountBase', 'account_base') ?? invoice?.accountBase,
            accountBaseName: getJournalValue(journal, 'accountBaseName', 'account_base_name') ?? invoice?.accountBaseName,
            accountRound: getJournalValue(journal, 'accountRound', 'account_round') ?? invoice?.accountRound,
            accountRoundName: getJournalValue(journal, 'accountRoundName', 'account_round_name') ?? invoice?.accountRoundName,
            accountTotal: getJournalValue(journal, 'accountTotal', 'account_total') ?? invoice?.accountTotal,
            accountTotalName: getJournalValue(journal, 'accountTotalName', 'account_total_name') ?? invoice?.accountTotalName,
            accountVAT: getJournalValue(journal, 'accountVAT', 'account_vat') ?? invoice?.accountVAT,
            accountVATName: getJournalValue(journal, 'accountVATName', 'account_vat_name') ?? invoice?.accountVATName,
        };

        const nextTransactions = Array.isArray(invoice?.TRANSACTION)
            ? (shouldRemap
                ? remapTransactionAccountsByVatTitle(invoice.TRANSACTION, nextHeaderAccounts)
                : invoice.TRANSACTION)
            : invoice?.TRANSACTION;

        onChange({
            ...invoice,
            journal: journal.id,
            journalName: journal.name,
            journalTreeHandle: treeHandle ?? invoice?.journalTreeHandle,
            accountBase: shouldRemap ? nextHeaderAccounts.accountBase : invoice?.accountBase,
            accountBaseName: shouldRemap ? nextHeaderAccounts.accountBaseName : invoice?.accountBaseName,
            accountRound: shouldRemap ? nextHeaderAccounts.accountRound : invoice?.accountRound,
            accountRoundName: shouldRemap ? nextHeaderAccounts.accountRoundName : invoice?.accountRoundName,
            accountTotal: shouldRemap ? nextHeaderAccounts.accountTotal : invoice?.accountTotal,
            accountTotalName: shouldRemap ? nextHeaderAccounts.accountTotalName : invoice?.accountTotalName,
            accountVAT: shouldRemap ? nextHeaderAccounts.accountVAT : invoice?.accountVAT,
            accountVATName: shouldRemap ? nextHeaderAccounts.accountVATName : invoice?.accountVATName,
            templateAcc: getJournalValue(journal, 'templateAcc', 'template_acc') ?? invoice?.templateAcc,
            TRANSACTION: nextTransactions,
        });
    };

    const applyJournalDefaults = (journal: any) => {
        if (!journal) return;
        if (!isFormEditable) return;

        const currentJournalId = String(invoice?.journal ?? "");
        const nextJournalId = String(journal?.id ?? "");
        const journalChanged = currentJournalId !== nextJournalId;

        // Ask only in update mode.
        if (!!isEdit && journalChanged) {
            setPendingJournal(journal);
            setIsJournalConfirmOpen(true);
            return;
        }

        // Add mode: apply defaults directly.
        // Detail mode is blocked above by isFormEditable guard.
        commitJournalChange(journal, !isEdit);
    };

    const handleJournalConfirmYes = () => {
        if (pendingJournal) {
            commitJournalChange(pendingJournal, true);
        }
        setPendingJournal(null);
        setIsJournalConfirmOpen(false);
    };

    const handleJournalConfirmNo = () => {
        if (pendingJournal) {
            commitJournalChange(pendingJournal, false);
        }
        setPendingJournal(null);
        setIsJournalConfirmOpen(false);
    };

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds)
    }

    return (
        <Flex direction={{ base: 'column', L: 'column' }} UNSAFE_className={styles.main_container} >
            {!showMorefileds ? (
                <Flex direction={'column'}>
                    <Flex justifyContent={'end'} UNSAFE_className={styles.toggle_row}>
                        <Link UNSAFE_className={styles.show_more_button} onPress={handleShowMorefields} isQuiet>
                            {_tt("show_more", "Show more")}
                        </Link>
                    </Flex>
                    <Flex direction={{ base: 'column', L: 'row' }} gap={'size-100'} UNSAFE_className={`${styles.form_row} ${styles.compact_row}`}>
                        <JournalDropDown
                            journalType={SELECTED_INVOICE?.journalType}
                            onChange={applyJournalDefaults}
                            selectedJournal={invoice?.journal}
                            selectedJournalName={invoice?.journalName}
                            isEdit={isEdit}
                            isDisabled={false}
                            isReadOnly={!isFormEditable}
                        />
                        <InvoiceCurrencyDropDown
                            label={exchangeRateLabel}
                            selectedCurrency={invoice?.currency}
                            onChange={(currency) => { onChange({ ...invoice, currency: currency?.code }) }}
                        />
                        <TextField
                            label={_t("external_number", "External Number")}
                            width={'100%'}
                            value={invoice?.externalNumber}
                            isReadOnly={!isFormEditable}
                            onChange={(val) => { onChange({ ...invoice, externalNumber: val }) }} />
                        <DatePicker
                            label={_t("issue_date", "Issue Date")}
                            width={'100%'}
                            value={readDateValue(invoice?.issuedDate)}
                            isReadOnly={!isFormEditable}
                            onChange={handleIssueDateChange} />
                        <DatePicker
                            label={secondaryDateLabel}
                            width={'100%'}
                            value={getSecondaryDateValue()}
                            isReadOnly={!isFormEditable}
                            onChange={setSecondaryDate}
                        />
                    </Flex>
                </Flex>
            ) : (
                <Flex direction={'column'} gap={'size-100'}>
                    <Flex justifyContent={'end'} UNSAFE_className={styles.toggle_row}>
                        <Link UNSAFE_className={styles.show_more_button} onPress={handleShowMorefields} isQuiet>
                            {_tt("show_less", "Show less")}
                        </Link>
                    </Flex>
                    <Flex direction={{ base: 'column', L: 'row' }} gap={'size-100'} wrap UNSAFE_className={styles.form_row}>
                        <JournalDropDown
                            journalType={SELECTED_INVOICE?.journalType}
                            onChange={applyJournalDefaults}
                            selectedJournal={invoice?.journal}
                            selectedJournalName={invoice?.journalName}
                            isEdit={isEdit}
                            isDisabled={false}
                            isReadOnly={!isFormEditable}
                        />
                        <TextField
                            label={_t("number", "Number")}
                            value={invoice?.invoiceNumber}
                            isReadOnly={!isFormEditable}
                            onChange={(val) => { onChange({ ...invoice, invoiceNumber: val }) }}
                            width={'100%'} />
                        <DatePicker
                            label={_t("issue_date", "Issue Date")}
                            width={'100%'}
                            value={readDateValue(invoice?.issuedDate)}
                            isReadOnly={!isFormEditable}
                            onChange={handleIssueDateChange} />
                        <DatePicker
                            label={secondaryDateLabel}
                            width={'100%'}
                            value={getSecondaryDateValue()}
                            isReadOnly={!isFormEditable}
                            onChange={setSecondaryDate}
                        />
                    </Flex>
                    <Flex direction={{ base: 'column', L: 'row' }} gap={'size-100'} wrap UNSAFE_className={styles.form_row}>
                        <Flex
                            direction={{ base: 'column', L: 'row' }}
                            width={{ base: '100%', L: '100%' }}
                            gap={'size-100'}
                            alignItems={{ base: 'stretch', L: 'end' }}
                        >
                            <TextField
                            label={_t("rate", "Rate")}
                            width={{ base: '100%', L: '30%' }}
                            value={invoice?.currencyRate}
                            isReadOnly={!isFormEditable}
                            onChange={(val) => { onChange({ ...invoice, currencyRate: val }) }} />
                            <InvoiceCurrencyDropDown
                                width={{ base: '100%', L: '70%' }}
                                label={exchangeRateLabel}
                                selectedCurrency={invoice?.currency}
                                onChange={(currency) => { onChange({ ...invoice, currency: currency?.code }) }} />
                        </Flex>
                        <TextField
                            label={_t("external_number", "External Number")}
                            width={'100%'}
                            value={invoice?.externalNumber}
                            isReadOnly={!isFormEditable}
                            onChange={(val) => { onChange({ ...invoice, externalNumber: val }) }} />
                        {isInvoiceDoc && (
                            <DatePicker
                                label={_t("taxation_date", "Taxation Date")}
                                width={'100%'}
                                value={readDateValue(invoice?.taxationDate)}
                                isReadOnly={!isFormEditable}
                                onChange={(e) =>
                                    onChange({ ...invoice, taxationDate: e?.toString() })
                                }
                            />
                        )}
                        {isInvoiceDoc && (
                            <DatePicker
                                label={_t("transaction_date", "Transaction Date")}
                                width={'100%'}
                                value={readDateValue(invoice?.dateTransaction)}
                                isReadOnly={!isFormEditable}
                                onChange={(e) =>
                                    onChange({ ...invoice, dateTransaction: e?.toString() })
                                }
                            />
                        )}
                    </Flex>
                </Flex>
            )}
            <DialogContainer onDismiss={handleJournalConfirmNo}>
                {isJournalConfirmOpen && (
                    <Dialog>
                        <Heading>{_t("update_default_gl_accounts", "Update Default GL Accounts")}</Heading>
                        <Content>
                            <View UNSAFE_className={styles.journal_confirm_line}></View>
                            <View UNSAFE_className={styles.journal_confirm_text}>
                                {_t("confirm_update_default_gl_accounts", "Do you want to update default GL accounts for invoice header and N/T/S/R transactions?")}
                            </View>
                            <Flex justifyContent="end" gap="size-100" marginTop="size-200">
                                <Button variant="secondary" UNSAFE_className={styles.journal_confirm_no} onPress={handleJournalConfirmNo}>
                                    {_tt("no", "No")}
                                </Button>
                                <Button variant="cta" UNSAFE_className={styles.journal_confirm_yes} onPress={handleJournalConfirmYes}>
                                    {_tt("yes", "Yes")}
                                </Button>
                            </Flex>
                        </Content>
                    </Dialog>
                )}
            </DialogContainer>
        </Flex>
    )

}

export default InvoiceDetailSection
